import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRouter } from "src/hooks/use-router";
import { paths } from "src/paths";
import { SplashScreen } from "src/components/splash-screen";
import { slackHook } from "src/utils/snug/slackHooks";
import useUserDataProvider from "src/contexts/snug/userData-context";

const SELF_SERVE_KEY = "snug.onboarding.self-serve";

interface OnboardingGuardProps {
  children: ReactNode;
}

export const OnboardingGuard: FC<OnboardingGuardProps> = ({ children }) => {
  const router = useRouter();
  const location = useLocation();
  const { userData, userDataInitialLoading, professionalGroup } = useUserDataProvider();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Wait for initial user data load
    if (userDataInitialLoading) return;
    if (userData === null) return;

    // User has a pro group role but pro group hasn't loaded yet
    if (userData.professional_group_role_user_data !== null && professionalGroup === null) return;

    // No pro group — should not happen; alert and send to account creation
    if (!userData.professional_group_role_user_data) {
      slackHook(
        `🚨 NO PRO GROUP: Authenticated user has no professional group!\n` +
          `ud_id: ${userData.ud_id}\n` +
          `email: ${userData.contact_email}`
      );
      router.replace(paths.getStarted.account);
      return;
    }

    // Has pro group — check payment
    if (!professionalGroup!.onboarding_payment_date) {
      router.replace(paths.getStarted.payment);
      return;
    }

    // Has payment — check onboarding completion
    if (!professionalGroup!.onboarding_completed_at) {
      const selfServe = localStorage.getItem(SELF_SERVE_KEY) === "true";
      if (selfServe) {
        // Self-serve users must be on /upgrade during onboarding
        if (location.pathname !== paths.dashboard.upgrade) {
          router.replace(paths.dashboard.upgrade);
          return;
        }
      } else {
        router.replace(paths.getStarted.onboarding);
        return;
      }
    }

    setChecked(true);
  }, [userDataInitialLoading, userData, professionalGroup, location.pathname, router]);

  if (!checked) {
    return <SplashScreen />;
  }

  return <>{children}</>;
};
